import { Drawer, message, Select } from "antd";
import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "../../../components/Component";
import { Col, Spinner } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormCheckbox from "../../form-components/CheckboxComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { tagRender } from "../projects/projectCreation";
import debounce from "lodash.debounce";
import { phaseApis } from "../../../api/Phase";
import { ApiResponse } from "../../../api/master/axiosIndex";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { projectsApis } from "../../../api/projects";
import { PhaseFlagEnum } from "./PhasesListView";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import useSequenceNumbering from "../../../hooks/useSequenceNumbering";
import {
  SequenceInput,
  SequenceConfigModal,
} from "../../../components/sequence";

export interface PhaseCreation {
  projectId: Number;
  phaseId?: number;
  phaseName: string;
  phaseOwnerId: number;
  phaseStartDate: string;
  phaseEndDate: string;
  flag: PhaseFlagEnum;
  phaseWeightage: number;
  phaseTags: {
    tagName?: string;
    tagId?: Number;
  }[];
}
const PhaseCreation: FC<{
  projectId: number;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  refreshData: () => void;
}> = ({ projectId, modalOpen, setModalOpen, refreshData }) => {
  const {
    data: {
      projectResponse: {
        name: projectName,
        plannedStartDate: projectStartDate,
        plannedEndDate: projectEndDate,
      },
    },
  } = useSelector((state: RootState) => state.PhaseInDetailModal);

  const [messageApi, contextHolder] = message.useMessage();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const methods = useForm<{
    phaseName: string;
    clonePhase: boolean;
    phaseStartDate: any;
    phaseEndDate: any;
    projectId: number | string;
    phaseId?: number | string;
    phaseOwnerId?: number | string;
    flag: PhaseFlagEnum;
    phaseWeightage: number | null;
  }>({
    mode: "onChange",
    defaultValues: {
      flag: "Internal",
      phaseWeightage: null,
    },
  });
  const [projectTagsList, setProjectTagsList] = useState<any[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [projectUsers, setProjectUsers] = useState<SelectOption[]>([]);
  const [options, setOptions] = useState<
    { label: string; value: string | number }[]
  >([]);
  const [remainingWeightage, setRemainingWeightage] = useState<number | null>(
    null
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    // formState: { errors, dirtyFields },
  } = methods;
  const cloneCheckboxWatch = watch("clonePhase");
  const startDateWatch = watch("phaseStartDate");

  // Phase numbering: auto-generate when a "Phases" sequence is configured for
  // this project, otherwise fall back to manual entry so creation isn't blocked.
  const {
    numberingMode,
    showNumberingModal,
    tempNumberingMode,
    setTempNumberingMode,
    activeSequences,
    currentSequence,
    canAuto,
    fetchSequenceAccess,
    openModal,
    closeModal,
    saveModalSelection,
  } = useSequenceNumbering({
    moduleName: "Phases",
    fieldName: "phaseSequenceId",
    level: "Project",
    projectId,
    setValue: setValue as any,
    getValues: getValues as any,
  });

  const fetchTagSuggestions = async (search: string) => {
    if (!search || search.length < 1) {
      // When search is empty, show all tags from projectTagsList
      const formatted =
        projectTagsList?.map((tag) => ({
          label: tag?.tagName,
          value: tag?.tagId,
        })) || [];
      setOptions(formatted);
      return;
    }
    const simulatedAPIResponse = projectTagsList?.filter((tag) =>
      tag?.tagName?.toLowerCase()?.includes(search?.toLowerCase())
    );
    const formatted = simulatedAPIResponse?.map((tag) => ({
      label: tag?.tagName,
      value: tag?.tagId,
    }));

    if (formatted.length === 0) {
      setOptions([
        {
          label: search,
          value: "newTag__" + search,
        },
      ]);
    } else {
      setOptions(formatted);
    }
  };

  const fetchProjectUsers = async () => {
    try {
      const response: any = await projectsApis.fetchProjectUsers(projectId);
      const {
        data: usersList,
        status,
      }: {
        status: boolean;
        data: { userId: string; userName: string; projectUserId: number }[];
      } = response || {};
      if (status) {
        const formatted = usersList?.map(
          (user: { userId: string; userName: string }) => ({
            label: user?.userName,
            value: +user?.userId,
          })
        );
        setProjectUsers(formatted);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const fetchProjectTags = async () => {
    try {
      const response: any = await projectsApis.fetchProjectTags(projectId);
      const {
        data: tagsList,
        status,
      }: {
        status: boolean;
        data: { tagName: string; tagId: number }[];
      } = response || {};
      if (status) {
        setProjectTagsList(tagsList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPhaseWeightages = async () => {
    try {
      const response: any = await phaseApis.getProjectPhaseWeightage(projectId);
      const {
        data: phasesWeightage,
        status,
      }: {
        status: boolean;
        data: { phaseId: number; phaseWeightage: number }[];
      } = response || {};
      if (status) {
        const total = (phasesWeightage || []).reduce(
          (sum, item) => sum + (Number(item?.phaseWeightage) || 0),
          0
        );
        const remaining = Math.max(0, 100 - total);
        setRemainingWeightage(remaining);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const submitNewPhase = async (data: any) => {
    try {
      setDisableSubmit(true);
      const {
        phaseName,
        clonePhase,
        phaseStartDate,
        phaseEndDate,
        flag,
        phaseWeightage,
      } = data;
      const payload: Partial<PhaseCreation> = {
        phaseName,
        projectId: projectId,
        phaseOwnerId: data.phaseOwnerId,
        flag: flag,
        phaseWeightage: phaseWeightage || 0,
        phaseTags: tags?.map((tag: any) => {
          if (typeof tag?.value === "string") {
            return {
              tagName: tag?.label,
            };
          }
          return {
            tagId: Number(tag?.value),
            tagName: tag?.label,
          };
        }),
      };
      if (phaseStartDate)
        payload.phaseStartDate = dayjs(phaseStartDate).format("YYYY-MM-DD");
      if (phaseEndDate)
        payload.phaseEndDate = dayjs(phaseEndDate).format("YYYY-MM-DD");
      // Send the phase number only when entered manually (auto mode lets the
      // server assign it from the sequence master).
      if (numberingMode === "manual" && data.phaseSequenceId) {
        (payload as any).phaseSequenceId = data.phaseSequenceId;
      }
      if (clonePhase) {
        payload.projectId = data.projectId;
        payload.phaseId = data.phaseId;
      }
      const response: ApiResponse = await phaseApis.createPhase(payload);
      const { data: phase, status, error, message } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: phase?.message,
          confirmButtonText: "Ok",
        });
        reset();
        setTags([]);
        refreshData();
        setModalOpen(false);
      } else {
        messageApi.open({
          type: "error",
          content: error + ": " + message,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDisableSubmit(false);
    }
  };

  const debouncedFetch = debounce(fetchTagSuggestions, 100);

  useEffect(() => {
    if (projectId && modalOpen) {
      fetchProjectUsers();
      fetchProjectTags();
      fetchPhaseWeightages();
      fetchSequenceAccess();
    }
  }, [projectId, modalOpen, fetchSequenceAccess]);

  // Populate options when projectTagsList is loaded so dropdown shows tags on open
  useEffect(() => {
    if (projectTagsList?.length > 0) {
      const formatted = projectTagsList.map((tag) => ({
        label: tag?.tagName,
        value: tag?.tagId,
      }));
      setOptions(formatted);
    }
  }, [projectTagsList]);

  return (
    <Drawer
      maskClosable={false}
      title={
        <div className="d-flex justify-content-between align-items-center">
          <strong>New Phase Creation for {projectName} </strong>
          <>
            <div style={{ textAlign: "right" }}>
              {disableSubmit ? (
                <Button color="primary" type="button">
                  <Spinner size="sm" color="light" />
                </Button>
              ) : (
                <AddAccess moduleName="Phases">
                  <Button
                    onClick={handleSubmit(submitNewPhase)}
                    color="primary"
                    className="m-3 mt-0 mb-0 ps-4 pe-4"
                    size="sm"
                  >
                    <span>Save</span>
                  </Button>
                </AddAccess>
              )}
            </div>
          </>
        </div>
      }
      placement="right"
      width={1200}
      onClose={() => {
        reset();
        setTags([]);
        setDisableSubmit(false);
        setRemainingWeightage(null);
        setModalOpen(false);
      }}
      open={modalOpen}
      styles={{ body: { paddingBottom: 80 } }}
    >
      {contextHolder}
      {modalOpen && (
        <FormProvider {...methods}>
          <form
            className="content clearfix mt-2"
            // onSubmit={}
          >
            <div className="row g-3">
              <div className="col-4 ">
                <FormInput
                  name="phaseName"
                  label="Phase Name"
                  required={true}
                  type="text"
                  placeholder="Enter Phase Name"
                />
                <FormCheckbox
                  name="clonePhase"
                  optionLabel="Clone From Existing Project"
                  noGap={true}
                  size={"small"}
                  onChange={(e) => {}}
                  className="mt-1"
                  disabled
                />
              </div>
              <div className="col-4">
                <SequenceInput
                  name="phaseSequenceId"
                  label="Phase Number"
                  numberingMode={numberingMode}
                  onSettingsClick={openModal}
                />
              </div>
              {cloneCheckboxWatch ? (
                <>
                  <div className="col-4">
                    <FormSelect
                      name="projectId"
                      label="Project"
                      required={true}
                      placeholder="Select Project"
                      options={[
                        { label: "Project 1", value: 1 },
                        { label: "Project 2", value: 2 },
                        { label: "Project 3", value: 3 },
                      ]}
                    />
                  </div>
                  <div className="col-4">
                    <FormSelect
                      name="phaseId"
                      label="Phase"
                      required={true}
                      placeholder="Select Phase"
                      options={[
                        { label: "Phase 1", value: 1 },
                        { label: "Phase 2", value: 2 },
                        { label: "Phase 3", value: 3 },
                      ]}
                    />
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
            <h6 className="mt-4">Phase Information</h6>
            <div className="row g-3">
              <div className="col-3">
                <FormSelect
                  name="phaseOwnerId"
                  label="Owner"
                  placeholder="Select Owner"
                  options={projectUsers}
                  required={true}
                />
              </div>
              <div className="col-3">
                <FormSelect
                  name="flag"
                  label="Flag"
                  placeholder="Select Flag"
                  // required={true}
                  options={[
                    { label: "Internal", value: "Internal" },
                    { label: "External", value: "External" },
                  ]}
                />
              </div>
              <div className="col-3">
                <FormDatePicker
                  name="phaseStartDate"
                  label="Planned Start Date"
                  // required={true}
                  minDate={
                    projectStartDate ? new Date(projectStartDate) : undefined
                  }
                />
              </div>
              <div className="col-3">
                <FormDatePicker
                  name="phaseEndDate"
                  label="Planned End Date"
                  // required={true}
                  disabled={!startDateWatch}
                  minDate={startDateWatch}
                  maxDate={
                    projectEndDate ? new Date(projectEndDate) : undefined
                  }
                />
              </div>
            </div>
            <div className="row g-3 mt-1">
              <div className="col-3">
                <FormInput
                  name="phaseWeightage"
                  label={
                    <span>
                      Weightage (%)
                      {remainingWeightage !== null && (
                        <span
                          style={{
                            color: "#52c41a",
                            marginLeft: "8px",
                            fontWeight: "normal",
                          }}
                        >
                          (Avl: {remainingWeightage}%)
                        </span>
                      )}
                    </span>
                  }
                  placeholder="Enter Weightage %"
                  type="text"
                  pattern={/^(?:100|[1-9][0-9]?)$/}
                  patternErrorMessage="Enter a value between 1 to 100"
                  customValidation={{
                    validate: (value: string) => {
                      if (
                        remainingWeightage === null ||
                        value === undefined ||
                        value === null ||
                        value === ""
                      )
                        return true;
                      const n = Number(value);
                      if (Number.isNaN(n)) return true;
                      return (
                        n <= remainingWeightage ||
                        `Cannot exceed available (${remainingWeightage}%)`
                      );
                    },
                  }}
                />
              </div>
            </div>
            <div className="row g-3 mt-1">
              <Col lg="12">
                <label className="form-label">Tags</label>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Type and press enter to create or select a tag"
                  labelInValue
                  value={tags}
                  onSearch={debouncedFetch}
                  onDropdownVisibleChange={(open) => {
                    if (open) fetchTagSuggestions("");
                  }}
                  onChange={(value) => {
                    const latest: any = value?.slice(-1)[0]; // Get the last tag

                    if (latest?.label?.length < 2) {
                      // Check if the last tag length is less than 2 characters
                      messageApi.open({
                        type: "error",
                        content: "Enter at least 2 characters",
                        duration: 5,
                      });
                      setOptions([]);
                      return;
                    }
                    setTags(value);
                  }}
                  options={options}
                  tokenSeparators={[","]}
                  tagRender={tagRender}
                  filterOption={false} // Use custom filtering only
                />
              </Col>
            </div>
          </form>
          <SequenceConfigModal
            moduleName="Phases"
            fieldLabel="Phase Number"
            show={showNumberingModal}
            tempNumberingMode={tempNumberingMode}
            setTempNumberingMode={setTempNumberingMode}
            canAuto={canAuto}
            currentSequence={currentSequence}
            activeSequences={activeSequences}
            onSave={saveModalSelection}
            onClose={closeModal}
          />
        </FormProvider>
      )}
    </Drawer>
  );
};

export default PhaseCreation;
